import { useState, useEffect } from 'react';
import { Switch, Tabs } from 'antd';
import UserItem from 'components/items/UserItem';
import EditButton from 'components/RestActions/EditButton';
import ShowButton from 'components/RestActions/ShowButton';
import { STATUS } from 'configs/localData';
import { pick } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { editUsers } from '@redux/users/actions';
import ActionGroup from 'components/RestActions/ActionGroup';
import DeleteButton from 'components/RestActions/DeleteButton';
import RestFieldItem from 'components/RestField/RestFieldItem';
import List from '../../rest/List';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';

const UsersList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const roles = useSelector((state) => state.config.data?.roles) || [];
  const [activeTab, setActiveTab] = useState('all');

  // Get role filter from URL or default to 'all'
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roleFilter = params.get('role');
    if (roleFilter) {
      setActiveTab(roleFilter);
    }
  }, [location.search]);

  // Create tabs based on available roles
  const getTabs = () => {
    const tabs = [
      {
        key: 'all',
        label: t('users.tabs.all') || 'All',
        filter: {},
      },
    ];

    // Add tabs for each role from config
    if (roles && Array.isArray(roles) && roles.length > 0) {
      roles.forEach((role) => {
        const roleValue = role.value || role;
        const roleLabel = role.label || role.text || roleValue;
        tabs.push({
          key: roleValue,
          label: roleLabel,
          filter: {
            role: {
              $eq: roleValue,
            },
          },
        });
      });
    } else {
      // Fallback to default roles if config doesn't have roles
      tabs.push(
        {
          key: 'admin',
          label: t('users.tabs.admin') || 'Admin',
          filter: {
            role: { $eq: 'admin' },
          },
        },
        {
          key: 'candidate',
          label: t('users.tabs.candidate') || 'Candidate',
          filter: {
            role: { $eq: 'candidate' },
          },
        },
        {
          key: 'company',
          label: t('users.tabs.company') || 'Company',
          filter: {
            role: { $eq: 'company' },
          },
        },
      );
    }

    return tabs;
  };

  const tabs = getTabs();
  const currentTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Update URL with role filter
    const params = new URLSearchParams(location.search);
    if (key === 'all') {
      params.delete('role');
    } else {
      params.set('role', key);
    }
    history.push(`${location.pathname}?${params.toString()}`);
  };

  // Prepare tabs items for Ant Design v5
  const tabItems = tabs.map((tab) => ({
    key: tab.key,
    label: tab.label,
  }));

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        style={{ marginBottom: 16 }}
      />
      <List
        redirects={{}}
        hasCreate={false}
        resource="users"
        initialFilter={{
          filter: currentTab.filter,
        }}
        key={activeTab} // Force re-render when tab changes
      >
        <RestFieldItem
          sorter
          hasSearch
          source="fullName"
          header="users.fullName"
          format={(data, record) => <UserItem record={record} />}
        />
        <RestFieldItem sorter hasSearch source="email" header="users.email" />
        <RestFieldItem
          sorter
          source="role"
          header="users.role"
          filters={
            roles && Array.isArray(roles) && roles.length > 0
              ? roles.map((role) => ({
                  text: role.label || role.text || role.value || role,
                  value: role.value || role,
                }))
              : [
                  { text: t('users.tabs.admin'), value: 'admin' },
                  { text: t('users.tabs.candidate'), value: 'candidate' },
                  { text: t('users.tabs.company'), value: 'company' },
                ]
          }
        />
        <RestFieldItem
          valueProp="checked"
          filters={STATUS}
          customOnChange={(value, record) => {
            dispatch(
              editUsers({
                data: {
                  ...pick(record, ['id', 'role', 'firstName', 'lastName']),
                  isGenie: !record.isGenie,
                },
                id: record.id,
                options: { isBack: false },
              }),
            );
          }}
          component={<Switch />}
          source="isActive"
          header="users.isActive"
        />
        <ActionGroup width={20} icon="ic-more">
          <ShowButton />
          <EditButton />
          <DeleteButton />
        </ActionGroup>
      </List>
    </div>
  );
};

UsersList.propTypes = {};

export default UsersList;
